import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import config from '../config';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Settings, Users2 } from 'lucide-react';
import { useSelectedMetrics, getCosClassName, getCpaClassName, useComponentMetrics } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, renderVariation } from '../utils/variationUtils';
import { colonnesVideoGoogleAds, cumulerVideo, TOTAUX_VIDEO_INITIAUX } from '../utils/metriquesVideo';
import { getDateRangeFromPeriod, calculatePreviousPeriod } from '../utils/periodUtils';
/**
 * Composant modulaire pour afficher les données par genre
 * Ce composant charge ses propres données depuis l'endpoint modulaire
 */
const GenderTableModular = React.memo(({
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  formatNumber,
  isVisible = true,
  onToggleVisibility,
  selectedCampaignFilter,
  selectedCampaignTypeFilter,
  token,
  setError,
  // Nouvelles props pour optimisation filtrage
  rawData = null,
  enableClientSideFiltering = false,
  preloadedData = null,
  // Props pour filtrage par labels
  selectedLabelsFilter = [],
  labelFilteredCampaignIds = null,
  // Props pour comparaison de périodes
  previousPeriodData = null,
  compareEnabled = false,
  comparisonType = '',
  loadingComparison = false,
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'gender'
}) => {
  const selectedMetrics = useSelectedMetrics();

  // États locaux pour les données
  const [genderData, setGenderData] = useState([]);
  const [previousGenderData, setPreviousGenderData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPreviousPeriod, setLoadingPreviousPeriod] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // États pour le tri
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Hook pour les métriques du composant
  const componentMetrics = useComponentMetrics('gender', selectedAccount?.id || 'global');

  // Helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isComponentMetricSelected = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // Couleurs pour l'histogramme (même style que l'histogramme "📈 Résultats")
  const CLICK_COLOR = '#82ca9d'; // Vert comme dans l'histogramme Résultats
  const CONVERSION_COLOR = '#8884d8'; // Mauve comme dans l'histogramme Résultats





  // Configuration du rendu des métriques (utilisé pour l'ordre dynamique)
  const metricConfig = useMemo(() => ({
    impressions: {
      label: 'Impressions',
      sortKey: 'impressions',
      renderCell: (gender, prevGender, hasComparison) => (
        <>
          {formatNumber(gender.impressions || 0)}
          {hasComparison && renderVariation(calculateVariation(gender.impressions || 0, prevGender.impressions || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.impressions)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.impressions, previousTotals.impressions))}
        </>
      )
    },
    ctr: {
      label: 'CTR',
      sortKey: 'ctr',
      renderCell: (gender, prevGender, hasComparison) => (
        <>
          {formatNumber(gender.ctr || 0, 2)}%
          {hasComparison && renderVariation(calculateVariation(gender.ctr || 0, prevGender.ctr || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.ctr, 2)}%</strong>
          {previousTotals && renderVariation(calculateVariation(totals.ctr, previousTotals.ctr))}
        </>
      )
    },
    clics: {
      label: 'Clics',
      sortKey: 'clicks',
      renderCell: (gender, prevGender, hasComparison) => (
        <>
          {formatNumber(gender.clicks || 0)}
          {hasComparison && renderVariation(calculateVariation(gender.clicks || 0, prevGender.clicks || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.clicks)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.clicks, previousTotals.clicks))}
        </>
      )
    },
    cpc: {
      label: 'CPC',
      sortKey: 'cpc',
      renderCell: (gender, prevGender, hasComparison) => (
        <>
          €{formatNumber(gender.cpc || 0, 2)}
          {hasComparison && renderVariation(calculateVariation(gender.cpc || 0, prevGender.cpc || 0), true)}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>€{formatNumber(totals.cpc, 2)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cpc, previousTotals.cpc), true)}
        </>
      )
    },
    cout: {
      label: 'Coût',
      sortKey: 'cost',
      renderCell: (gender, prevGender, hasComparison) => (
        <>
          €{formatNumber(gender.cost || 0, 2)}
          {hasComparison && renderVariation(calculateVariation(gender.cost || 0, prevGender.cost || 0), true)}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>€{formatNumber(totals.cost, 2)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cost, previousTotals.cost), true)}
        </>
      )
    },
    taux_conv: {
      label: 'Taux conv.',
      sortKey: 'conversionRate',
      renderCell: (gender, prevGender, hasComparison) => {
        const conversionRate = gender.conversionRate || (gender.clicks > 0 ? (gender.conversions / gender.clicks * 100) : 0);
        const prevConversionRate = prevGender
          ? (prevGender.conversionRate || (prevGender.clicks > 0 ? (prevGender.conversions / prevGender.clicks * 100) : 0))
          : 0;
        return (
          <>
            {formatNumber(conversionRate, 2)}%
            {hasComparison && renderVariation(calculateVariation(conversionRate, prevConversionRate))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.conversionRate, 2)}%</strong>
          {previousTotals && renderVariation(calculateVariation(totals.conversionRate, previousTotals.conversionRate))}
        </>
      )
    },
    conv: {
      label: 'Conv.',
      sortKey: 'conversions',
      renderCell: (gender, prevGender, hasComparison) => (
        <>
          {formatNumber(gender.conversions || 0, 1)}
          {hasComparison && renderVariation(calculateVariation(gender.conversions || 0, prevGender.conversions || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.conversions, 1)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.conversions, previousTotals.conversions))}
        </>
      )
    },
    cpa: {
      label: 'CPA',
      sortKey: 'cpa',
      getClassName: (gender) => getCpaClassName(gender.cpa || 0, selectedAccount?.id || 'global', moduleId),
      getTotalClassName: (totals) => getCpaClassName(totals.cpa, selectedAccount?.id || 'global', moduleId),
      renderCell: (gender, prevGender, hasComparison) => {
        const cpa = gender.cpa || 0;
        const prevCpa = prevGender && prevGender.conversions > 0 ? (prevGender.cost / prevGender.conversions) : 0;
        return (
          <>
            {cpa >= 0 ? `€${formatNumber(cpa, 2)}` : '-'}
            {hasComparison && renderVariation(calculateVariation(cpa, prevCpa), true)}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{totals.cpa >= 0 ? `€${formatNumber(totals.cpa, 2)}` : '-'}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cpa, previousTotals.cpa), true)}
        </>
      )
    },
    valeur_conv: {
      label: 'Valeur Conv.',
      sortKey: 'conversionsValue',
      renderCell: (gender, prevGender, hasComparison) => (
        <>
          €{formatNumber(gender.conversionsValue || 0, 2)}
          {hasComparison && renderVariation(calculateVariation(gender.conversionsValue || 0, prevGender.conversionsValue || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>€{formatNumber(totals.conversionsValue, 2)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.conversionsValue, previousTotals.conversionsValue))}
        </>
      )
    },
    cos: {
      label: 'COS',
      sortKey: 'cos',
      getClassName: (gender) => getCosClassName(gender.cos || 0, selectedAccount?.id || 'global', moduleId),
      getTotalClassName: (totals) => getCosClassName(totals.cos, selectedAccount?.id || 'global', moduleId),
      renderCell: (gender, prevGender, hasComparison) => {
        const cos = gender.cos || 0;
        const prevCos = prevGender && (prevGender.conversionsValue || 0) > 0 ? (prevGender.cost / (prevGender.conversionsValue || 1) * 100) : 0;
        return (
          <>
            {formatNumber(cos, 1)}%
            {hasComparison && renderVariation(calculateVariation(cos, prevCos), true)}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.cos, 1)}%</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cos, previousTotals.cos), true)}
        </>
      )
    },
    roas: {
      label: 'ROAS',
      sortKey: 'roas',
      renderCell: (gender, prevGender, hasComparison) => {
        const roas = (gender.cost || 0) > 0 ? ((gender.conversionsValue || 0) / gender.cost * 100) : 0;
        const prevRoas = prevGender && (prevGender.cost || 0) > 0 ? ((prevGender.conversionsValue || 0) / prevGender.cost * 100) : 0;
        return (
          <>
            {formatNumber(roas, 1)}%
            {hasComparison && renderVariation(calculateVariation(roas, prevRoas))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const roas = (totals.cost || 0) > 0 ? ((totals.conversionsValue || 0) / totals.cost * 100) : 0;
        const prevRoas = previousTotals && (previousTotals.cost || 0) > 0 ? ((previousTotals.conversionsValue || 0) / previousTotals.cost * 100) : 0;
        return (
          <>
            <strong>{formatNumber(roas, 1)}%</strong>
            {previousTotals && renderVariation(calculateVariation(roas, prevRoas))}
          </>
        );
      }
    },
    roi: {
      label: 'ROI',
      sortKey: 'roi',
      renderCell: (gender, prevGender, hasComparison) => {
        const roi = (gender.cost || 0) > 0 ? (((gender.conversionsValue || 0) - gender.cost) / gender.cost) : 0;
        const prevRoi = prevGender && (prevGender.cost || 0) > 0 ? (((prevGender.conversionsValue || 0) - prevGender.cost) / prevGender.cost) : 0;
        return (
          <>
            {formatNumber(roi, 2)}
            {hasComparison && renderVariation(calculateVariation(roi, prevRoi))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const roi = (totals.cost || 0) > 0 ? (((totals.conversionsValue || 0) - totals.cost) / totals.cost) : 0;
        const prevRoi = previousTotals && (previousTotals.cost || 0) > 0 ? (((previousTotals.conversionsValue || 0) - previousTotals.cost) / previousTotals.cost) : 0;
        return (
          <>
            <strong>{formatNumber(roi, 2)}</strong>
            {previousTotals && renderVariation(calculateVariation(roi, prevRoi))}
          </>
        );
      }
    },
    panier_moyen: {
      label: 'Panier moyen',
      sortKey: 'panierMoyen',
      renderCell: (gender, prevGender, hasComparison) => {
        const panierMoyen = (gender.conversions || 0) > 0 ? (gender.conversionsValue || 0) / gender.conversions : 0;
        const prevPanierMoyen = prevGender && (prevGender.conversions || 0) > 0 ? (prevGender.conversionsValue || 0) / prevGender.conversions : 0;
        return (
          <>
            €{formatNumber(panierMoyen, 2)}
            {hasComparison && renderVariation(calculateVariation(panierMoyen, prevPanierMoyen))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const panierMoyen = (totals.conversions || 0) > 0 ? (totals.conversionsValue || 0) / totals.conversions : 0;
        const prevPanierMoyen = previousTotals && (previousTotals.conversions || 0) > 0 ? (previousTotals.conversionsValue || 0) / previousTotals.conversions : 0;
        return (
          <>
            <strong>€{formatNumber(panierMoyen, 2)}</strong>
            {previousTotals && renderVariation(calculateVariation(panierMoyen, prevPanierMoyen))}
          </>
        );
      }
    },
    ...colonnesVideoGoogleAds({ formatNumber, renderVariation, calculateVariation })
  }), [formatNumber, selectedAccount?.id, renderVariation, calculateVariation]);

  // Charger les données quand le composant est monté ou que les dépendances changent
  useEffect(() => {
    // Ne pas charger les données si le module est masqué
    if (!isVisible) return;

    // Fonction pour charger les données de comparaison
    const loadPreviousPeriodData = async () => {
      if (!compareEnabled || !selectedAccount || !token) {
        setPreviousGenderData([]);
        return;
      }

      // Utiliser previousPeriodData.period si disponible (cohérence avec App.js)
      let prevPeriod;
      if (previousPeriodData?.period?.startDate && previousPeriodData?.period?.endDate) {
        prevPeriod = {
          startDate: previousPeriodData.period.startDate,
          endDate: previousPeriodData.period.endDate
        };
      } else if (comparisonType) {
        // Fallback: calculer la période précédente
        let currentStartDate = customStartDate;
        let currentEndDate = customEndDate;
        if (!currentStartDate || !currentEndDate) {
          const dateRange = getDateRangeFromPeriod(selectedPeriod);
          currentStartDate = dateRange.startDate;
          currentEndDate = dateRange.endDate;
        }
        prevPeriod = calculatePreviousPeriod(currentStartDate, currentEndDate, comparisonType);
      } else {
        setPreviousGenderData([]);
        return;
      }

      console.log(`👫 [GENDER-API] Chargement période précédente: ${prevPeriod.startDate} à ${prevPeriod.endDate}`);

      try {
        const prevRequestBody = {
          sources: [selectedAccount.id],
          period: 'CUSTOM',
          campaignFilter: selectedCampaignFilter || null,
          campaignTypeFilter: selectedCampaignTypeFilter || null,
          customStartDate: prevPeriod.startDate,
          customEndDate: prevPeriod.endDate
        };

        if (labelFilteredCampaignIds && labelFilteredCampaignIds.length > 0) {
          prevRequestBody.campaignIds = labelFilteredCampaignIds;
        }

        const prevResponse = await axios.post(
          `${config.API_URL}/api/dashboard/components/gender`,
          prevRequestBody,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (prevResponse.data.success) {
          const prevGenders = prevResponse.data.data.genders || [];
          console.log(`👫 [GENDER-API] ${prevGenders.length} genres période précédente`);
          setPreviousGenderData(prevGenders);
        }
      } catch (prevError) {
        console.warn(`⚠️ [GENDER-API] Erreur chargement période précédente:`, prevError.message);
        setPreviousGenderData([]);
      }
    };

    // Mode preload : utiliser les données déjà chargées en parallèle (seulement si aucun filtre)
    if (preloadedData && preloadedData.success && !selectedCampaignFilter && !selectedCampaignTypeFilter && selectedLabelsFilter.length === 0) {
      const genders = preloadedData.data.genders || [];
      console.log(`👫 [GENDER-FRONTEND] Utilisation données préchargées: ${genders.length} genres`);
      setGenderData(genders);
      setIsDataLoaded(true);
      setLoading(false);
      // Charger les données de comparaison si activé
      if (compareEnabled && previousPeriodData?.period?.startDate && previousPeriodData?.period?.endDate) {
        loadPreviousPeriodData();
      } else {
        setPreviousGenderData([]);
      }
      return;
    }

    // Mode optimisé : utiliser rawData avec filtrage côté client (seulement si pas de labels)
    if (enableClientSideFiltering && rawData && selectedLabelsFilter.length === 0) {
      // Extraire les données par genre depuis rawData
      const accountData = Object.values(rawData).find(data => data && data.genders);
      const allGenderData = accountData?.genders || [];

      setGenderData(allGenderData);
      setIsDataLoaded(true);
      setLoading(false);
      // Pas de données précédentes en mode client-side, reset
      setPreviousGenderData([]);
      return;
    }

    // Mode classique : appel API modulaire
    if (!selectedAccount || !selectedPeriod || !token) {
      return;
    }

    const loadGenderData = async () => {
      try {
        setLoading(true);
        const requestBody = {
          sources: [selectedAccount.id],
          period: selectedPeriod,
          campaignFilter: selectedCampaignFilter || null,
          campaignTypeFilter: selectedCampaignTypeFilter || null
        };

        // Ajouter les campaignIds filtrés par labels si présents
        if (labelFilteredCampaignIds && labelFilteredCampaignIds.length > 0) {
          requestBody.campaignIds = labelFilteredCampaignIds;
          console.log('👫 [GENDER-API] Filtrage par labels - campaignIds:', labelFilteredCampaignIds.length);
        }

        // Ajouter les dates personnalisées si période CUSTOM
        if (selectedPeriod === 'CUSTOM') {
          if (!customStartDate || !customEndDate) return;
          requestBody.customStartDate = customStartDate;
          requestBody.customEndDate = customEndDate;
        }

        const response = await axios.post(
          `${config.API_URL}/api/dashboard/components/gender`,
          requestBody,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.success) {
          const genders = response.data.data.genders || [];
          setGenderData(genders);
          setIsDataLoaded(true);

          // Charger les données de la période précédente si comparaison activée
          if (compareEnabled && comparisonType) {
            // Calculer les dates de la période actuelle
            let currentStartDate = customStartDate;
            let currentEndDate = customEndDate;
            if (!currentStartDate || !currentEndDate) {
              const dateRange = getDateRangeFromPeriod(selectedPeriod);
              currentStartDate = dateRange.startDate;
              currentEndDate = dateRange.endDate;
            }
            const prevPeriod = calculatePreviousPeriod(currentStartDate, currentEndDate, comparisonType);
            console.log(`👫 [GENDER-API] Chargement période précédente: ${prevPeriod.startDate} à ${prevPeriod.endDate}`);

            try {
              const prevRequestBody = {
                sources: [selectedAccount.id],
                period: 'CUSTOM',
                campaignFilter: selectedCampaignFilter || null,
                campaignTypeFilter: selectedCampaignTypeFilter || null,
                customStartDate: prevPeriod.startDate,
                customEndDate: prevPeriod.endDate
              };

              // Ajouter les campaignIds filtrés par labels si présents
              if (labelFilteredCampaignIds && labelFilteredCampaignIds.length > 0) {
                prevRequestBody.campaignIds = labelFilteredCampaignIds;
              }

              const prevResponse = await axios.post(
                `${config.API_URL}/api/dashboard/components/gender`,
                prevRequestBody,
                {
                  headers: { Authorization: `Bearer ${token}` }
                }
              );

              if (prevResponse.data.success) {
                const prevGenders = prevResponse.data.data.genders || [];
                console.log(`👫 [GENDER-API] ${prevGenders.length} genres période précédente`);
                setPreviousGenderData(prevGenders);
              }
            } catch (prevError) {
              console.warn(`⚠️ [GENDER-API] Erreur chargement période précédente:`, prevError.message);
              setPreviousGenderData([]);
            }
          } else {
            setPreviousGenderData([]);
          }
        } else {
          setError('Erreur lors du chargement des données par genre');
        }
      } catch (error) {
        setError('Erreur lors du chargement des données par genre');
      } finally {
        setLoading(false);
      }
    };

    loadGenderData();
  }, [isVisible, selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedCampaignFilter, selectedCampaignTypeFilter, token, setError, preloadedData, selectedLabelsFilter, labelFilteredCampaignIds, compareEnabled, comparisonType, previousPeriodData?.period?.startDate, previousPeriodData?.period?.endDate]);

  // Les données sont déjà filtrées côté serveur (via campaignIds)
  // Pas de filtrage client-side car les données par genre sont agrégées sans campaignId
  const filteredGenderData = useMemo(() => {
    return genderData;
  }, [genderData]);

  // Fonction de tri memoized
  const sortedGenderData = useMemo(() => {
    if (!filteredGenderData.length) return [];

    const sorted = [...filteredGenderData];
    sorted.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'gender') {
        // Pour les genres, tri logique : Homme -> Femme -> autres
        const genderOrder = {
          'Homme': 1,
          'Femme': 2,
          'Non déterminé': 3,
          'Non spécifié': 4,
          'Inconnu': 5,
          'Genre inconnu': 6
        };

        aValue = genderOrder[aValue] || 999;
        bValue = genderOrder[bValue] || 999;

        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Pour les valeurs numériques
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;

      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [filteredGenderData, sortConfig]);

  // Pagination des données triées
  const totalPages = Math.ceil(sortedGenderData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedGenderData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedGenderData, currentPage, itemsPerPage]);

  // Réinitialiser la pagination quand les données changent
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredGenderData]);

  // Calcul des totaux memoized (sur les données filtrées)
  const totals = useMemo(() => {
    if (!filteredGenderData.length) return {};

    const baseTotals = filteredGenderData.reduce((acc, gender) => ({
      impressions: acc.impressions + (gender.impressions || 0),
      clicks: acc.clicks + (gender.clicks || 0),
      cost: acc.cost + (gender.cost || 0),
      conversions: acc.conversions + (gender.conversions || 0),
      conversionsValue: acc.conversionsValue + (gender.conversionsValue || 0),
      ...cumulerVideo(acc, gender)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, ...TOTAUX_VIDEO_INITIAUX });

    // Calculer les métriques dérivées
    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [filteredGenderData]);

  // Calcul des totaux de la période précédente
  const previousTotals = useMemo(() => {
    if (!compareEnabled || !previousGenderData.length) return null;

    const baseTotals = previousGenderData.reduce((acc, gender) => ({
      impressions: acc.impressions + (gender.impressions || 0),
      clicks: acc.clicks + (gender.clicks || 0),
      cost: acc.cost + (gender.cost || 0),
      conversions: acc.conversions + (gender.conversions || 0),
      conversionsValue: acc.conversionsValue + (gender.conversionsValue || 0),
      ...cumulerVideo(acc, gender)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, ...TOTAUX_VIDEO_INITIAUX });

    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [compareEnabled, previousGenderData]);

  // Données pour l'histogramme mixte (tous les genres avec données)
  const chartData = useMemo(() => {
    if (!filteredGenderData.length) return [];

    // Tri par ordre logique : Homme -> Femme -> autres
    const genderOrder = {
      'Homme': 1,
      'Femme': 2,
      'Non déterminé': 3,
      'Non spécifié': 4,
      'Inconnu': 5,
      'Genre inconnu': 6
    };

    return filteredGenderData
      .filter(gender => gender.conversions > 0 || gender.clicks > 0)
      .sort((a, b) => {
        const orderA = genderOrder[a.gender] || 999;
        const orderB = genderOrder[b.gender] || 999;
        return orderA - orderB;
      })
      .map(gender => {
        const genderName = gender.gender || 'Inconnu';
        const genderString = typeof genderName === 'string' ? genderName : String(genderName || 'Inconnu');

        return {
          genre: genderString,
          clics: gender.clicks || 0,
          conversions: Math.round(gender.conversions) || 0
        };
      });
  }, [filteredGenderData]);

  // Handler de tri memoized
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'desc' ? 'asc' : 'desc'
    }));
  }, []);

  // Détecter si Performance Max est sélectionné (via type de campagne ou campagne individuelle)
  const isPerformanceMaxSelected =
    selectedCampaignTypeFilter?.type === 10 ||
    selectedCampaignTypeFilter?.name === 'Performance Max' ||
    (selectedCampaignFilter && (
      selectedCampaignFilter.name?.includes('PMAX') ||
      selectedCampaignFilter.name?.includes('Performance Max') ||
      selectedCampaignFilter.type === 10 ||
      selectedCampaignFilter.advertisingChannelType === 10
    ));

  // Si Performance Max est sélectionné, afficher un message informatif au lieu de masquer
  if (isPerformanceMaxSelected) {
    return (
      <div className="campaigns-table-section">
        <div className="section-header">
          <h4><Users2 size={16} />&nbsp;Genre <span style={{ fontSize: '0.7em', fontWeight: 'normal', color: '#888' }}>(hors Performance Max)</span></h4>
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Genre"
            />
          ) : (
            <button
              onClick={onToggleVisibility}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
        {isVisible && (
          <div className="table-container">
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#666',
              background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
              borderRadius: '8px',
              border: '1px solid #ddd'
            }}>
              <div style={{ fontSize: '2em', marginBottom: '15px' }}>🚫</div>
              <h4 style={{ margin: '0 0 10px 0', color: '#555' }}>Données démographiques non disponibles</h4>
              <p style={{ margin: '0 0 15px 0', fontSize: '0.95em', maxWidth: '500px', marginLeft: 'auto', marginRight: 'auto' }}>
                Les campagnes <strong>Performance Max</strong> ne fournissent pas de données démographiques détaillées (genre, âge).
              </p>
              <p style={{ margin: '0', fontSize: '0.85em', fontStyle: 'italic', color: '#888' }}>
                💡 Sélectionnez un autre type de campagne pour voir les données par genre.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Ne pas afficher le composant si pas de données (même pendant le loading)
  if (isDataLoaded && filteredGenderData.length === 0) {
    return null;
  }

  // Si pas encore chargé et pas en cours de loading, ne pas afficher non plus
  if (!loading && !isDataLoaded) {
    return null;
  }

  // Pour les clients : masquer complètement si le module est fermé
  if (userRole === 'CLIENT' && !isVisible) {
    return null;
  }

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><Users2 size={16} />&nbsp;Genre <span style={{ fontSize: '0.7em', fontWeight: 'normal', color: '#888' }}>(hors Performance Max)</span></h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Genre"
            />
          ) : (
            <button
              onClick={onToggleVisibility}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <>
          {loading ? (
            <div className="loading-message" style={{ textAlign: 'center' }}>
              <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
              Chargement des données par genre...<br /><br />
            </div>
          ) : compareEnabled && (loadingComparison || loadingPreviousPeriod) ? (
            <div className="loading-message" style={{ textAlign: 'center', padding: '20px' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 10px' }}></div>
              Chargement des données de comparaison...
            </div>
          ) : !isDataLoaded ? (
            <div className="loading-message" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <div>
                <h4 style={{ margin: '0 0 10px 0', color: '#888' }}>👫 Genre</h4>
                <p style={{ margin: '0', fontSize: '0.9em' }}>
                  Sélectionnez un compte et une période pour voir les données démographiques.
                </p>
              </div>
            </div>
          ) : filteredGenderData.length === 0 ? (
            <div className="loading-message" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <div>
                <h4 style={{ margin: '0 0 10px 0', color: '#888' }}>👫 Aucune donnée démographique</h4>
                <p style={{ margin: '0', fontSize: '0.9em' }}>
                  {selectedCampaignFilter && (selectedCampaignFilter.name?.includes('PMAX') || selectedCampaignFilter.name?.includes('Performance Max'))
                    ? 'Les campagnes Performance Max ne fournissent pas de données démographiques détaillées dans l\'API Google Ads.'
                    : 'Aucune donnée par genre trouvée pour la période sélectionnée.'
                  }
                </p>
                {selectedCampaignFilter && (selectedCampaignFilter.name?.includes('PMAX') || selectedCampaignFilter.name?.includes('Performance Max')) && (
                  <p style={{ margin: '10px 0 0 0', fontSize: '0.8em', fontStyle: 'italic' }}>
                    💡 Essayez de sélectionner une campagne Search ou Shopping pour voir les données démographiques.
                  </p>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Histogramme mixte clics/conversions */}
              {chartData.length > 0 && (
                <div
                  style={{
                    width: '100%',
                    height: '400px',
                    background: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    marginBottom: '20px'
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartData}
                      margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 20
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="genre"
                        stroke="#6b7280"
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={40}
                        interval={0}
                      />
                      <YAxis
                        yAxisId="left"
                        orientation="left"
                        stroke={CLICK_COLOR}
                        tick={{ fontSize: 12 }}
                        label={{ value: 'Clics', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        stroke={CONVERSION_COLOR}
                        tick={{ fontSize: 12 }}
                        label={{ value: 'Conversions', angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                      />
                      <Tooltip
                        formatter={(value, name) => [
                          formatNumber(value),
                          name === 'Clics' ? 'Clics' : 'Conversions'
                        ]}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}
                        cursor={{ fill: '#f9fafb' }}
                      />
                      <Legend />
                      <Bar
                        dataKey="clics"
                        yAxisId="left"
                        fill={CLICK_COLOR}
                        fillOpacity={0.8}
                        name="Clics"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="conversions"
                        yAxisId="right"
                        fill={CONVERSION_COLOR}
                        fillOpacity={0.8}
                        name="Conversions"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Tableau détaillé */}
              <div className="campaigns-table-wrapper">
                <table className="campaigns-table" key={selectedMetrics.join(',')} style={{ tableLayout: 'auto' }}>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('gender')} className="sortable">
                        Genre {sortConfig.key === 'gender' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      {/* Rendu dynamique des colonnes selon l'ordre configuré */}
                      {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                        const config = metricConfig[metricKey];
                        return (
                          <th key={metricKey} onClick={() => handleSort(config.sortKey)} className="sortable numeric">
                            {config.label} {sortConfig.key === config.sortKey && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan="11" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                          <div>
                            <h4 style={{ margin: '0 0 10px 0', color: '#888' }}>👫 Données démographiques non disponibles</h4>
                            <p style={{ margin: '0', fontSize: '0.9em' }}>
                              {selectedCampaignFilter && (selectedCampaignFilter.name?.includes('PMAX') || selectedCampaignFilter.name?.includes('Performance Max'))
                                ? 'Les campagnes Performance Max ne fournissent pas de données démographiques détaillées.'
                                : 'Aucune donnée par genre trouvée pour la période sélectionnée.'
                              }
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedData.map((gender, index) => {
                        // Trouver la ligne correspondante dans les données de la période précédente
                        const prevGender = compareEnabled && previousGenderData.length > 0
                          ? previousGenderData.find(p => p.gender === gender.gender)
                          : null;
                        const hasComparison = !!prevGender;

                        return (
                          <tr key={index}>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ marginRight: '8px', fontSize: '1.2em' }}>
                                  {(gender.gender || '') === 'Homme' ? '👨' :
                                   (gender.gender || '') === 'Femme' ? '👩' :
                                   (gender.gender || '') === 'Non déterminé' ? '❔' : '❓'}
                                </span>
                                {gender.gender || 'Genre inconnu'}
                              </div>
                            </td>
                            {/* Rendu dynamique des cellules selon l'ordre configuré */}
                            {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                              const config = metricConfig[metricKey];
                              const className = config.getClassName ? `numeric ${config.getClassName(gender)}` : 'numeric';
                              return (
                                <td key={metricKey} className={className}>
                                  {config.renderCell(gender, prevGender || {}, hasComparison)}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })
                    )}
                  </tbody>

                  {sortedGenderData.length > 0 && (
                    <tfoot>
                      <tr className="total-row">
                        <td className="device-name"><strong>TOTAL</strong></td>
                        {/* Rendu dynamique des totaux selon l'ordre configuré */}
                        {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                          const config = metricConfig[metricKey];
                          const className = config.getTotalClassName ? `numeric ${config.getTotalClassName(totals)}` : 'numeric';
                          return (
                            <td key={metricKey} className={className}>
                              {config.renderTotal(totals, previousTotals)}
                            </td>
                          );
                        })}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {/* Contrôles de pagination */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    <span>
                      Affichage {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, sortedGenderData.length)} sur {sortedGenderData.length} genres
                    </span>
                  </div>
                  <div className="pagination-buttons">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      ← Précédent
                    </button>
                    <span className="page-numbers">
                      Page {currentPage} sur {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      Suivant →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="gender"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Genre"
      />
    </div>
  );
});

GenderTableModular.displayName = 'GenderTableModular';

export default GenderTableModular;
